import { useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

import http from '../../shared/http'
import { TApi } from '../../shared/const'
import { formatVienna } from '../../shared/datetime'
import Btn from '../Btn'

import styles from './style.module.scss'

const DAY_CHOICES = Array.from({ length: 14 }, (_, index) => index + 1)

/**
 * Die Rückfrage „Habt ihr {{name}} schon kontaktiert?" (Anforderung #232).
 *
 * Die Antwort steht direkt in der Mitteilung: „Ja" beendet die Rückfrage,
 * „Noch nicht" öffnet die Auswahl „Wir werden … in den nächsten x Tagen
 * kontaktieren" (1–14). Nach x Tagen fragt die Junior Datenbank erneut.
 *
 * Ob schon geantwortet wurde, steht in `info.answer` — der Server schreibt es
 * in **alle** offenen Rückfragen derselben Zusammenarbeit, damit an einer
 * älteren Mitteilung keine Knöpfe mehr stehen. Hier wird es nach dem Absenden
 * zusätzlich lokal gesetzt, damit die Karte sofort umschaltet.
 */
export default function ContactCheck({ info }) {
	const { t } = useTranslation()

	const [answer, setAnswer] = useState(info?.answer || null)
	const [askDays, setAskDays] = useState(false)
	const [days, setDays] = useState(null)
	const [sending, setSending] = useState(false)

	const name = info?.supporterName || ''

	const send = body => {
		if (!info?.connectionId) return
		setSending(true)
		http.post(TApi.COMMON_SUPPORTER_CONTACT_CHECK(info.connectionId), body)
			.then(({ data }) => setAnswer(data))
			// Die Meldung kommt aus shared/http (etwa „Diese Zusammenarbeit besteht nicht mehr").
			.catch(() => {})
			.finally(() => setSending(false))
	}

	if (answer) {
		return (
			<p className={styles.contactCheckAnswer}>
				{answer.contacted
					? t('contactCheckAnsweredYes', { by: answer.byName || t('contactCheckSomeone'), name })
					: t('contactCheckAnsweredNo', {
							name,
							date: formatVienna(answer.promisedUntil || answer.at, 'dd.MM.yyyy')
					  })}
			</p>
		)
	}

	const reach = [info?.supporterEmail, info?.supporterPhone].filter(Boolean).join(', ')

	return (
		<div className={styles.contactCheck}>
			<p className={styles.contactCheckQuestion}>{t('contactCheckQuestion')}</p>
			<div className={styles.contactCheckButtons}>
				<Btn size="small" theme="success" disabled={sending} onClick={() => send({ contacted: true })}>
					{t('contactCheckYes')}
				</Btn>
				<Btn
					size="small"
					theme={askDays ? 'primary' : 'secondary'}
					disabled={sending}
					onClick={() => setAskDays(true)}
				>
					{t('contactCheckNo')}
				</Btn>
			</div>

			{askDays && (
				<fieldset className={styles.contactCheckDays}>
					<legend>{t('contactCheckPromise', { name, reach: reach ? `(${reach})` : '' })}</legend>
					<div className={styles.contactCheckChoices}>
						{DAY_CHOICES.map(value => (
							<label key={value} className={styles.contactCheckChoice}>
								<input
									type="radio"
									name={`contact-days-${info?.connectionId}`}
									value={value}
									checked={days === value}
									onChange={() => setDays(value)}
								/>
								<span>
									{value === 1 ? t('contactCheckDay') : t('contactCheckDays', { count: value })}
								</span>
							</label>
						))}
					</div>
					<Btn size="small" disabled={sending || !days} onClick={() => send({ contacted: false, days })}>
						{t('contactCheckSubmit')}
					</Btn>
				</fieldset>
			)}
		</div>
	)
}

ContactCheck.propTypes = {
	info: PropTypes.shape({
		connectionId: PropTypes.number,
		supporterName: PropTypes.string,
		supporterEmail: PropTypes.string,
		supporterPhone: PropTypes.string,
		answer: PropTypes.object
	})
}
